// LINKED MODES: a mode switcher entry that IS another module. Picking it goes to
// that module's own route, exactly as its nav menu link would: no iframe, no
// nested layout, no second header (BP-116).
//
// Aaron, 2026-10-18, on g2 /app/onboarding → Action Register: "action menu and
// reports and forms should just navigate to the module". Onboarding's Action
// Register and Reports & Forms modes iframed those modules' pages under the
// Onboarding layout, so the screen had two nav bars and two headers
// ("Onboarding / Action Register" over "Action Register / List").
//
// A module declares its linked modes as segment → ROUTER path (the target
// module's own route, no "/app": the router's basename adds it):
//
//   export const LINKED_MODES: LinkedModes = { "action-register": "/actionregister" };
//
// and its layout and route table use the three pieces below:
//   - modeLinkTarget(): the switcher entry's `to` — the linked route, or the
//     module's own <base>/<segment>;
//   - LinkedModeRedirect: the route element for <base>/<segment>, so a saved
//     URL, a top-nav child (navModeRegistry lists every mode) or a remembered
//     link still lands on the module. Declare it OUTSIDE the mode layout, so the
//     layout never renders on it and never records it as the module's mode;
//   - isInModuleMode(): a remembered mode is restored only when it is one of the
//     module's own (a stored linked segment would bounce a bare visit out of the
//     module).
//
// The path must be a route that EXISTS on every tenant the module mounts on.
// A route App.tsx generates from the live nav exists per tenant only (see
// useSectionReachability); a static <Route> in App.tsx exists everywhere.
import React from "react";
import { Navigate, useLocation } from "react-router-dom";

/** Mode segment → the router path of the module it opens ("/actionregister"). */
export type LinkedModes = Readonly<{ [segment: string]: string }>;

const has = (linked: LinkedModes | undefined, segment: string): boolean =>
  !!linked && Object.prototype.hasOwnProperty.call(linked, segment);

/** The switcher entry's target: the linked module's route, else `<base>/<segment>`. */
export function modeLinkTarget(basePath: string, segment: string, linked?: LinkedModes): string {
  return has(linked, segment) ? linked![segment] : `${basePath}/${segment}`;
}

/** Is this one of the module's own modes (not a link to another module)? */
export function isInModuleMode(segment: string, linked?: LinkedModes): boolean {
  return !has(linked, segment);
}

/** Route element for a linked mode's old segment: replace it with the module's route, query and hash kept. */
export const LinkedModeRedirect: React.FC<{ to: string }> = ({ to }) => {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
};
